const { getElement } = window.BAO.Utils

export class SiteDrawer extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.drawers = getElement('site-drawers')
    if (!this.drawers) {
      throw new Error(
        `${this.nodeName.toLowerCase()} needs a <site-drawers> element to work`,
      )
    }

    if (!this.elements.close) {
      throw new Error(
        `${this.nodeName.toLowerCase()} needs a [data-module-drawers-close=${
          this.key
        }] attribute to work`,
      )
    }

    this.drawers.drawers.push(this)

    this._isOpen = false
    this._triggerButton = null
  }

  connectedCallback () {
    super.connectedCallback()

    if (this.drawerShouldBeMoved) {
      window.setTimeout(() => {
        this.moveToElement.appendChild(this)
      }, parseInt(this.getAttribute('data-module-drawers-move-delay'), 10) || 0)
    }

    this.keydownHandler = e => this.handleKeyDown(e)
    this.addEventListener('keydown', this.keydownHandler)

    this.setupKeydownListeners()
    this.watchFocusElementsForChanges()
  }

  watchFocusElementsForChanges () {
    const targetNode = this
    const config = { childList: true, subtree: true }

    const callback = function (mutationsList, observer) {
      // eslint-disable-next-line no-unused-vars
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          this.setupKeydownListeners()
          return
        }
      }
    }

    const observer = new MutationObserver(callback.bind(this))

    observer.observe(targetNode, config)
  }

  handleKeyDown (e) {
    this.triggerButton = getElement(`[data-drawers-trigger=${this.key}]`)
    this.activeDrawerKey = this.key
    // Handling Tab for focus trapping

    if (this.isOpen && e.key === 'Tab') {
      this.trapFocus(e)
    }

    // Handling ESC to close the drawer
    if (this.isOpen && e.key === 'Escape') {
      this.closeDrawer()
    }
  }

  setupKeydownListeners () {
    // Find all labels within the drawer

    const labels = this.querySelectorAll('label')

    labels.forEach(label => {
      label.addEventListener('keydown', e => {
        // Check if Enter key is pressed
        if (e.key === 'Enter' || e.keyCode === 13) {
          // Find the associated input if any
          const input = label.querySelector(
            'input[type="checkbox"], input[type="radio"]',
          )
          if (input) {
            input.click() // Simulate a click on the input
            e.preventDefault() // Prevent any default action
          }
        }
      })
    })
  }

  closeDrawer () {
    this.isOpen = false

    this.classList.remove(this.config.classes.activeDrawerClass)
    this.drawers.activeDrawerKey = ''

    if (this.triggerButton) {
      this.triggerButton.focus()
    }
  }

  trapFocus (e) {
    const isTabPressed = e.key === 'Tab' || e.keyCode === 9

    if (!isTabPressed) {
      return
    }

    const focusableElements = Array.from(
      this.querySelectorAll(
        'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), summary',
      ),
    ).filter(el => {
      const details = el.closest('details')

      if (details && !details.open && !el.matches('summary')) {
        return false
      }

      return el.offsetParent !== null
    })

    if (focusableElements.length === 0) {
      return
    }

    const firstFocusableElement = focusableElements[0]
    const lastFocusableElement = focusableElements[focusableElements.length - 1]

    if (e.shiftKey) {
      if (document.activeElement === firstFocusableElement) {
        lastFocusableElement.focus()
        e.preventDefault()
      }
    } else {
      if (document.activeElement === lastFocusableElement) {
        firstFocusableElement.focus()
        e.preventDefault()
      }
    }
  }

  get key () {
    return this.getAttribute('key')
  }

  get elements () {
    return {
      trigger: getElement(`[data-drawers-trigger=${this.key}]`),
      close: getElement(`[data-module-drawers-close=${this.key}]`),
    }
  }

  get isOpen () {
    return this._isOpen
  }

  set isOpen (val) {
    this._isOpen = val
    this.classList.toggle(this.config.classes.activeDrawerClass, val)

    if (val) {
      window.setTimeout(() => {
        const focusableElements = this.querySelectorAll(
          'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])',
        )
        if (focusableElements.length > 0) {
          focusableElements[0].focus()
        }
      }, 200)
    }
  }

  get drawerShouldBeMoved () {
    return this.hasAttribute('data-module-drawers-move-me')
  }

  get moveToElement () {
    switch (this.getAttribute('data-module-drawers-move-me')) {
      case 'root':
        return this.drawers
      default:
        return getElement(this.getAttribute('data-module-drawers-move-me'))
    }
  }

  get config () {
    return {
      classes: {
        activeDrawerClass: 'drw-Drawer-active',
      },
    }
  }
}

customElements.define('site-drawer', SiteDrawer)
